// Optional recovery code (Implementation Plan v02 §6.2; key-custody.md). A long
// random code the person prints or saves; it wraps the same data key as the app
// lock (keys.ts, same Argon2id floor). It never leaves the device; no escrow (D-05).
//
// Format: 28 symbols of Crockford base32 (no I, L, O, U) = 140 bits, shown as
// 7 groups of 4. Typing is forgiving: case, spaces and dashes are ignored, and
// O → 0, I / L → 1 are mapped.

import { type Bytes, randomBytes, wipe } from './bytes'
import { secretBytes } from './keys'

const ALPHABET = '0123456789ABCDEFGHJKMNPQRSTVWXYZ'
const SYMBOLS = 28
const GROUP = 4

export function generateRecoveryCode(): string {
  const bytes = randomBytes(Math.ceil((SYMBOLS * 5) / 8))
  let bits = 0
  let value = 0
  let out = ''
  for (const byte of bytes) {
    value = (value << 8) | byte
    bits += 8
    while (bits >= 5 && out.length < SYMBOLS) {
      out += ALPHABET[(value >>> (bits - 5)) & 31]
      bits -= 5
    }
    value &= (1 << bits) - 1
  }
  wipe(bytes)
  return formatRecoveryCode(out)
}

export function formatRecoveryCode(normalized: string): string {
  const groups: string[] = []
  for (let i = 0; i < normalized.length; i += GROUP) {
    groups.push(normalized.slice(i, i + GROUP))
  }
  return groups.join('-')
}

/** Normalise what the person typed; null when it cannot be a recovery code. */
export function normalizeRecoveryCode(input: string): string | null {
  const cleaned = input
    .toUpperCase()
    .replace(/[\s-]/g, '')
    .replace(/O/g, '0')
    .replace(/[IL]/g, '1')
  if (cleaned.length !== SYMBOLS) {
    return null
  }
  for (const ch of cleaned) {
    if (!ALPHABET.includes(ch)) {
      return null
    }
  }
  return cleaned
}

/**
 * The ONE way to turn a recovery code into key-wrap bytes — used both at setup
 * (with the generated, dashed code) and at unlock (with what the person typed),
 * so the two always match. Null when the input cannot be a recovery code.
 */
export function recoveryCodeBytes(input: string): Bytes | null {
  const normalized = normalizeRecoveryCode(input)
  return normalized === null ? null : secretBytes(normalized)
}
